import { supabase } from "@/integrations/supabase/client";
import { FarmlyPlace } from "@/lib/types";

/**
 * Maps a raw Supabase row to FarmlyPlace type.
 */
function mapRowToPlace(row: Record<string, unknown>): FarmlyPlace {
  let placeCore: Record<string, any> | undefined;
  if (row.place_core) {
    if (typeof row.place_core === "string") {
      try { placeCore = JSON.parse(row.place_core); } catch { placeCore = undefined; }
    } else if (typeof row.place_core === "object") {
      placeCore = row.place_core as Record<string, any>;
    }
  }

  return {
    id: String(row.id),
    slug: String(row.slug || ""),
    name: String(row.name || ""),
    municipality: row.municipality ? String(row.municipality) : undefined,
    landskap: row.landskap ? String(row.landskap) : undefined,
    county: row.county ? String(row.county) : undefined,
    tags: Array.isArray(row.tags) ? row.tags.map(String) : [],
    description_short: String(row.description_short || ""),
    address_text: row.address_text ? String(row.address_text) : undefined,
    website: row.website ? String(row.website) : undefined,
    instagram: row.instagram ? String(row.instagram) : undefined,
    phone: row.phone ? String(row.phone) : undefined,
    contact_email: row.contact_email ? String(row.contact_email) : undefined,
    contact_website: row.contact_website ? String(row.contact_website) : undefined,
    contact_instagram: row.contact_instagram ? String(row.contact_instagram) : undefined,
    contact_phone: row.contact_phone ? String(row.contact_phone) : undefined,
    verified_at: row.verified_at ? String(row.verified_at) : undefined,
    lat: typeof row.lat === "number" ? row.lat : undefined,
    lng: typeof row.lng === "number" ? row.lng : undefined,
    place_core: placeCore,
  };
}

export type FetchPlacesResult = {
  places: FarmlyPlace[];
  error?: string;
};

export async function fetchPlacesSupabase(): Promise<FetchPlacesResult> {
  try {
    const { data, error } = await supabase
      .from("places")
      .select("*")
      .eq("status", "published")
      .order("name", { ascending: true })
      .limit(200);

    if (error) {
      console.error("Supabase fetch error:", error);
      return { places: [], error: `Kunde inte hämta platser: ${error.message}` };
    }

    if (!data || data.length === 0) return { places: [] };
    const places = data.map(mapRowToPlace);
    return { places };
  } catch (err) {
    console.error("Supabase fetch exception:", err);
    return { places: [], error: "Ett oväntat fel uppstod vid hämtning från Supabase." };
  }
}

export type FetchPlaceBySlugResult = {
  place: FarmlyPlace | null;
  error?: string;
};

export async function fetchPlaceBySlugSupabase(slug: string): Promise<FetchPlaceBySlugResult> {
  try {
    const { data, error } = await supabase
      .from("places")
      .select("*")
      .eq("slug", slug)
      .maybeSingle();

    if (error) {
      console.error("Supabase fetch place error:", error);
      return { place: null, error: `Kunde inte hämta platsen: ${error.message}` };
    }

    if (!data) return { place: null };
    return { place: mapRowToPlace(data) };
  } catch (err) {
    console.error("Supabase fetch place exception:", err);
    return { place: null, error: "Ett oväntat fel uppstod vid hämtning från Supabase." };
  }
}

export function hasSupabaseConfig(): boolean {
  return true;
}
